<template>
  <fieldset class="ex-client">
    <legend>{{ t('exclusiveUnits.dialog.clientHeading') }}</legend>

    <template v-if="mode === 'search'">
      <label
        :for="searchId"
        class="sr-only"
        >{{ t('exclusiveUnits.dialog.searchLabel') }}</label
      >
      <input
        :id="searchId"
        v-model="search"
        type="search"
        class="field"
        autocomplete="off"
        :placeholder="t('exclusiveUnits.dialog.searchPlaceholder')"
        :disabled="disabled"
      />
      <p
        v-if="isFetching && !clients.length"
        class="hint"
      >
        {{ t('exclusiveUnits.dialog.searching') }}
      </p>
      <p
        v-else-if="isError"
        class="hint"
      >
        {{ t('exclusiveUnits.dialog.clientsError') }}
      </p>
      <p
        v-else-if="!clients.length"
        class="hint"
      >
        {{ debouncedSearch ? t('exclusiveUnits.dialog.noMatch') : t('exclusiveUnits.dialog.noClientsYet') }}
      </p>
      <div
        v-else
        role="radiogroup"
        class="list"
        :aria-label="t('exclusiveUnits.dialog.clientsLabel')"
      >
        <label
          v-for="c in clients"
          :key="c.id"
          class="option"
          :class="{ picked: pickedId === c.id }"
        >
          <input
            type="radio"
            :name="radioName"
            :value="c.id"
            :checked="pickedId === c.id"
            :disabled="disabled"
            @change="pick(c)"
          />
          <span class="name">{{ c.name }}</span>
          <span
            class="phone"
            dir="ltr"
            >{{ c.phoneE164 }}</span
          >
        </label>
      </div>
      <button
        type="button"
        class="switch"
        :disabled="disabled"
        @click="setMode('add')"
      >
        {{ t('exclusiveUnits.dialog.addClient') }}
      </button>
    </template>

    <template v-else>
      <label
        :for="nameId"
        class="lbl"
        >{{ t('exclusiveUnits.dialog.nameLabel') }}</label
      >
      <input
        :id="nameId"
        v-model="quickName"
        type="text"
        class="field"
        autocomplete="off"
        :maxlength="CLIENT_NAME_MAX_LENGTH"
        :disabled="disabled"
      />
      <CommonPhoneInput
        :id="phoneId"
        v-model="quickPhone"
        v-model:valid="quickPhoneValid"
        :label="t('exclusiveUnits.dialog.phoneLabel')"
        required
      />
      <button
        type="button"
        class="switch"
        :disabled="disabled"
        @click="setMode('search')"
      >
        {{ t('exclusiveUnits.dialog.chooseExisting') }}
      </button>
    </template>

    <p class="privacy">{{ t('exclusiveUnits.dialog.privacy') }}</p>
  </fieldset>
</template>

<script setup lang="ts">
import { refDebounced } from '@vueuse/core';
import { CLIENT_NAME_MAX_LENGTH } from '~/constants/clients';
import type { ClientSummary } from '~/types/client';
import type { HoldClientChoice } from '~/utils/exclusives/holdDialog';
import { useHoldClientSearchQuery } from '~/api/queries/useHoldClientSearchQuery';

const { disabled = false } = defineProps<{
  disabled?: boolean;
}>();

const client = defineModel<HoldClientChoice | null>('client', { default: null });

const { t } = useI18n();
const searchId = useId();
const nameId = useId();
const phoneId = useId();
const radioName = useId();

const SEARCH_DEBOUNCE_MS = 300;
const mode = ref<'search' | 'add'>('search');
const search = ref('');
const debouncedSearch = refDebounced(search, SEARCH_DEBOUNCE_MS);

const { data, isFetching, isError } = useHoldClientSearchQuery(debouncedSearch, () => mode.value === 'search');
const clients = computed<ClientSummary[]>(() => data.value ?? []);

const pickedId = computed(() => (client.value?.kind === 'existing' ? client.value.clientId : null));

const pick = (c: ClientSummary) => {
  client.value = { kind: 'existing', clientId: c.id, name: c.name, phoneE164: c.phoneE164 ?? null };
};

const quickName = ref('');
const quickPhone = ref<string | null>(null);
const quickPhoneValid = ref(false);

const syncQuickAdd = () => {
  client.value = {
    kind: 'quick_add',
    name: quickName.value,
    phoneE164: quickPhone.value,
    phoneValid: quickPhoneValid.value,
  };
};
watch([quickName, quickPhone, quickPhoneValid], () => {
  if (mode.value === 'add') syncQuickAdd();
});

const setMode = (next: 'search' | 'add') => {
  mode.value = next;
  if (next === 'add') syncQuickAdd();
  else client.value = null;
};
</script>

<style scoped lang="scss">
.ex-client {
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  legend {
    font-size: 15px;
    font-weight: 700;
    color: #0d0f15;
    margin-bottom: 8px;
    padding: 0;
  }
}

.field {
  height: 40px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid rgba(13, 15, 21, 0.1);
  padding: 0 12px;
  font-size: 14px;
  color: #0d0f15;
  background: #fff;

  &:focus {
    outline: none;
    border-color: rgba(13, 15, 21, 0.4);
  }
}

.lbl {
  font-size: 14px;
  font-weight: 500;
  color: rgba(13, 15, 21, 0.8);
  margin-bottom: -6px;
}

.hint {
  font-size: 13px;
  color: rgba(13, 15, 21, 0.62);
  margin: 0;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}

.option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(13, 15, 21, 0.1);
  cursor: pointer;
  font-size: 14px;

  &.picked {
    border-color: #0d0f15;
    background: #f8f7f3;
  }

  input {
    accent-color: #0d0f15;
  }

  .name {
    font-weight: 600;
    color: #0d0f15;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .phone {
    font-size: 12px;
    color: rgba(13, 15, 21, 0.62);
  }
}

.switch {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  color: #b07410;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.privacy {
  font-size: 12px;
  color: rgba(13, 15, 21, 0.62);
  margin: 0;
}
</style>
